import type { ApiHuelle, Betrieb, Dokument, DokumentTyp, PortalHochgeladen } from "./typen";

const BASIS = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
const TOKEN_SCHLUESSEL = "anmeldemeister.token";

export class ApiFehler extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details: unknown = null,
    public readonly requestId: string | null = null,
  ) {
    super(message);
    this.name = "ApiFehler";
  }

  /** Feldfehler aus 422-Antworten: [{feld, meldung}] → {feld: meldung} */
  get feldFehler(): Record<string, string> {
    if (!Array.isArray(this.details)) return {};
    const ergebnis: Record<string, string> = {};
    for (const eintrag of this.details as { feld?: string; meldung?: string }[]) {
      if (eintrag.feld && eintrag.meldung) ergebnis[eintrag.feld] = eintrag.meldung;
    }
    return ergebnis;
  }
}

// Normal nur in sessionStorage (endet mit dem Tab). Mit „Angemeldet bleiben“ (nur Installateure, vom
// Server bestätigt) in localStorage, damit die App auf dem Handy nach dem Schließen angemeldet bleibt.
export const tokenSpeicher = {
  lesen(): string | null {
    try {
      return sessionStorage.getItem(TOKEN_SCHLUESSEL) ?? localStorage.getItem(TOKEN_SCHLUESSEL);
    } catch {
      return null;
    }
  },
  schreiben(token: string, dauerhaft = false): void {
    this.loeschen();
    try {
      (dauerhaft ? localStorage : sessionStorage).setItem(TOKEN_SCHLUESSEL, token);
    } catch {
      /* Speicher gesperrt – Token gilt dann nur bis zum Neuladen */
    }
  },
  loeschen(): void {
    try {
      sessionStorage.removeItem(TOKEN_SCHLUESSEL);
      localStorage.removeItem(TOKEN_SCHLUESSEL);
    } catch {
      /* ignorieren */
    }
  },
};

let abmeldeHandler: (() => void) | null = null;
export function beiSitzungsende(handler: (() => void) | null): void {
  abmeldeHandler = handler;
}

function kopfzeilen(json: boolean): Record<string, string> {
  const kopf: Record<string, string> = { Accept: "application/json" };
  const token = tokenSpeicher.lesen();
  if (token) kopf.Authorization = `Bearer ${token}`;
  if (json) kopf["Content-Type"] = "application/json";
  return kopf;
}

function fehlerAusAntwort(status: number, huelle: ApiHuelle<unknown> | null, requestId: string | null): ApiFehler {
  if (huelle?.error) {
    return new ApiFehler(status, huelle.error.code, huelle.error.message, huelle.error.details ?? null, huelle.request_id ?? requestId);
  }
  const standard =
    status === 502 || status === 503 || status === 504
      ? "Server nicht erreichbar – bitte in Kürze erneut versuchen"
      : `Unerwartete Antwort vom Server (HTTP ${status})`;
  return new ApiFehler(status, "http_error", standard, null, requestId);
}

function pruefeSitzung(fehler: ApiFehler, pfad: string): void {
  if (fehler.status === 401 && !pfad.startsWith("/api/auth/login")) {
    tokenSpeicher.loeschen();
    abmeldeHandler?.();
  }
}

export async function anfrage<T>(
  pfad: string,
  optionen: { methode?: "GET" | "POST" | "PUT"; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const { methode = "GET", body, signal } = optionen;
  let antwort: Response;
  try {
    antwort = await fetch(BASIS + pfad, {
      method: methode,
      headers: kopfzeilen(body !== undefined),
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiFehler(0, "network_error", "Server nicht erreichbar – bitte Internetverbindung prüfen");
  }

  const text = await antwort.text();
  let huelle: ApiHuelle<T> | null = null;
  try {
    huelle = text ? (JSON.parse(text) as ApiHuelle<T>) : null;
  } catch {
    huelle = null;
  }
  const requestId = antwort.headers.get("X-Request-ID");
  if (!antwort.ok || !huelle || huelle.status !== "success") {
    const fehler = fehlerAusAntwort(antwort.status, huelle as ApiHuelle<unknown> | null, requestId);
    pruefeSitzung(fehler, pfad);
    throw fehler;
  }
  return huelle.data as T;
}

/** Lädt geschützte Dateien (Vorschau, Thumbnails) mit Token als Blob. */
export async function blobLaden(pfad: string, signal?: AbortSignal): Promise<Blob> {
  let antwort: Response;
  try {
    antwort = await fetch(BASIS + pfad, { headers: kopfzeilen(false), signal });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiFehler(0, "network_error", "Datei konnte nicht geladen werden – Server nicht erreichbar");
  }
  if (!antwort.ok) {
    let huelle: ApiHuelle<unknown> | null = null;
    try {
      huelle = (await antwort.json()) as ApiHuelle<unknown>;
    } catch {
      huelle = null;
    }
    const fehler = fehlerAusAntwort(antwort.status, huelle, antwort.headers.get("X-Request-ID"));
    pruefeSitzung(fehler, pfad);
    throw fehler;
  }
  return antwort.blob();
}

export interface UploadParameter {
  datei: File;
  typ: DokumentTyp;
  kundeId: number;
  anlageId?: number | null;
}

export interface LaufenderUpload<T = Dokument> {
  ergebnis: Promise<T>;
  abbrechen: () => void;
}

/** Upload per XHR: im Gegensatz zu fetch mit Fortschrittsanzeige. */
function xhrHochladen<T>(pfad: string, formular: FormData, beiFortschritt: (prozent: number) => void): LaufenderUpload<T> {
  const xhr = new XMLHttpRequest();
  const ergebnis = new Promise<T>((resolve, reject) => {
    xhr.open("POST", BASIS + pfad);
    const token = tokenSpeicher.lesen();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("Accept", "application/json");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) beiFortschritt(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let huelle: ApiHuelle<T> | null = null;
      try {
        huelle = JSON.parse(xhr.responseText) as ApiHuelle<T>;
      } catch {
        huelle = null;
      }
      if (xhr.status >= 200 && xhr.status < 300 && huelle?.status === "success" && huelle.data) {
        resolve(huelle.data);
        return;
      }
      const fehler = fehlerAusAntwort(xhr.status, huelle as ApiHuelle<unknown> | null, xhr.getResponseHeader("X-Request-ID"));
      pruefeSitzung(fehler, pfad);
      reject(fehler);
    };
    xhr.onerror = () => reject(new ApiFehler(0, "network_error", "Upload fehlgeschlagen – Server nicht erreichbar"));
    xhr.onabort = () => reject(new ApiFehler(0, "abgebrochen", "Upload abgebrochen"));
    xhr.send(formular);
  });
  return { ergebnis, abbrechen: () => xhr.abort() };
}

/** Backoffice-Upload. Nach 100 % läuft serverseitig noch die Extraktion („Extrahiere Daten…“). */
export function dokumentHochladen(param: UploadParameter, beiFortschritt: (prozent: number) => void): LaufenderUpload {
  const formular = new FormData();
  formular.append("file", param.datei, param.datei.name);
  formular.append("kunde_id", String(param.kundeId));
  if (param.anlageId) formular.append("anlage_id", String(param.anlageId));
  return xhrHochladen<Dokument>(`/api/dokumente/hochladen?typ=${encodeURIComponent(param.typ)}`, formular, beiFortschritt);
}

/** Portal-Upload zu einer eigenen Anmeldung. Die Antwort enthält nie Erkennungsdaten. */
export function portalDokumentHochladen(
  param: { datei: File; typ: DokumentTyp; anlageId: number },
  beiFortschritt: (prozent: number) => void,
): LaufenderUpload<PortalHochgeladen> {
  const formular = new FormData();
  formular.append("file", param.datei, param.datei.name);
  return xhrHochladen<PortalHochgeladen>(
    `/api/portal/anlagen/${param.anlageId}/dokumente?typ=${encodeURIComponent(param.typ)}`,
    formular,
    beiFortschritt,
  );
}

/** Backoffice: Logo eines Betriebs hochladen (wird serverseitig als PNG neu gespeichert). */
export function betriebLogoHochladen(betriebId: number, datei: File): Promise<Betrieb> {
  const formular = new FormData();
  formular.append("file", datei, datei.name);
  return xhrHochladen<Betrieb>(`/api/betriebe/${betriebId}/logo`, formular, () => undefined).ergebnis;
}
